/**
 * HighlightOverlay — Liquid glass element highlight effect.
 *
 * Draws a glowing border around a target DOM element to draw attention.
 * Uses accent color glow instead of hardcoded blue.
 */

import { injectTokens, tokens } from '../tokens'
import { springOpacity, type SpringAnimation } from '../spring'

const PULSE_KEYFRAMES = `
@keyframes hs-highlight-glow {
  0%, 100% { box-shadow: 0 0 8px var(--hs-accent-glow), inset 0 0 4px var(--hs-accent-glow); }
  50% { box-shadow: 0 0 20px var(--hs-accent-glow), inset 0 0 8px var(--hs-accent-glow); }
}
`

let pulseStyleInjected = false

interface ActiveHighlight {
  overlay: HTMLDivElement
  timer: ReturnType<typeof setTimeout>
  scrollHandler: () => void
  anim: SpringAnimation | null
}

export class GlassHighlightRenderer {
  private highlights: ActiveHighlight[] = []

  /** Highlight a DOM element with a glowing border. */
  highlight(el: HTMLElement, durationMs = 3000): void {
    injectTokens()
    this.injectPulseStyle()

    const overlay = document.createElement('div')
    overlay.className = 'hs-highlight-overlay'
    overlay.setAttribute('data-hs-theme', 'dark')

    const updatePosition = () => {
      const rect = el.getBoundingClientRect()
      Object.assign(overlay.style, {
        position: 'fixed',
        top: `${rect.top - 3}px`,
        left: `${rect.left - 3}px`,
        width: `${rect.width + 6}px`,
        height: `${rect.height + 6}px`,
        border: '2px solid var(--hs-accent)',
        borderRadius: 'var(--hs-radius-sm)',
        animation: 'hs-highlight-glow 1.5s ease-in-out infinite',
        pointerEvents: 'none',
        zIndex: tokens.zOverlay,
        boxSizing: 'border-box',
      })
    }

    updatePosition()
    document.body.appendChild(overlay)

    const scrollHandler = () => updatePosition()
    window.addEventListener('scroll', scrollHandler, { passive: true })

    const timer = setTimeout(() => this.removeHighlight(entry), durationMs)
    const entry: ActiveHighlight = { overlay, timer, scrollHandler, anim: null }
    this.highlights.push(entry)
  }

  /** Remove all highlights. */
  clearAll(): void {
    this.highlights.forEach(h => this.removeHighlight(h))
    this.highlights = []
  }

  private removeHighlight(entry: ActiveHighlight): void {
    clearTimeout(entry.timer)
    window.removeEventListener('scroll', entry.scrollHandler)

    entry.anim = springOpacity(entry.overlay, 1, 0, 'smooth', () => {
      entry.overlay.remove()
    })

    const idx = this.highlights.indexOf(entry)
    if (idx >= 0) this.highlights.splice(idx, 1)
  }

  private injectPulseStyle(): void {
    if (pulseStyleInjected) return
    pulseStyleInjected = true

    const style = document.createElement('style')
    style.textContent = PULSE_KEYFRAMES
    document.head.appendChild(style)
  }
}
